/**
 * PublicFormLayout — the shell a prospective client sees.
 * @file src/web/shared/PublicFormLayout.tsx
 *
 * Deliberately NOT the marketing shell and NOT the dashboard shell.
 *
 * The person here has no account, is not a customer, and arrived from an
 * emailed link or an iframe on the firm's own website. A header offering
 * "Sign in", "Dashboard" or product navigation is noise at best and, in an
 * embed, actively wrong — the surrounding page is the firm's, not ours.
 *
 * So: the mark, a single line of trust copy, and the form. Nothing to click
 * except the questions.
 *
 * Its two strings follow the language the page inside it declared, via
 * <html lang>. They used to be hard-coded English, which put an Arabic form
 * under an English promise about encryption — the exact half-translation the
 * form itself now refuses to commit.
 *
 * It also owns the <head> for these routes, because none of the pages inside
 * it set one. Without that, a client opening their own intake form met the
 * marketing title in the browser tab — "Client intake software for law firms"
 * — which is an advertisement in the one place a person who is already here
 * cannot want it. And `noindex` because these are private by intent:
 * robots.txt refuses /f/, /s/ and /book/ already, but a Disallow only stops a
 * crawler that reads it, and the cost of saying it twice is one meta tag.
 *
 * @llm-rule WHEN: Any route a non-authenticated client fills in
 * @llm-rule AVOID: Adding navigation, auth controls or marketing links here
 * @llm-rule AVOID: Wrapping it in AuthGuard — that is the whole point
 * @llm-rule AVOID: Dropping noindex — these URLs are somebody's private link
 */

import { Outlet } from 'react-router-dom';
import { NaumeneMark } from './NaumeneMark';
import { usePublicBrand } from './publicBrand';
import { apiAsset } from './api';
import { LAYOUT_UI } from './i18n';
import { useDocumentLanguage } from './hooks/useDocumentLanguage';
import { useSEO } from './hooks/useSEO';
import { languageDir } from '@shared/languages';

export function PublicFormLayout() {
  const lang = useDocumentLanguage();
  const t = LAYOUT_UI[lang];
  /* Whose form this is, published by the page inside. Until it loads there
   * is no firm, and the header shows our own mark rather than a gap. */
  const brand = usePublicBrand();

  /* In the client's own language, and out of the index. */
  useSEO({ title: t.secure, description: t.disclaimer, noindex: true });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-2xl items-center gap-2.5 px-4 py-4 sm:px-6">
          {/*
            * Nothing until the page says whose this is.
            *
            * Showing our mark first and swapping it for the firm's is worse
            * than showing neither for a moment: the client sees a brand
            * they do not recognise on the page where they are deciding
            * whether to trust it, and the firm sees their logo arrive
            * second.
            */}
          {!brand.known ? (
            <span className="h-8 w-40 animate-pulse rounded bg-muted" aria-hidden="true" />
          ) : brand.logoUrl ? (
            <img
              src={apiAsset(brand.logoUrl)}
              alt={brand.name ?? ''}
              /* Bounded rather than sized: a firm's logo may be a tall crest
               * or a wide wordmark, and neither should be stretched into the
               * other's shape. */
              className="max-h-8 w-auto max-w-[12rem] object-contain"
            />
          ) : (
            <NaumeneMark className="size-7" />
          )}
          {/* A logo is a wordmark more often than not, so repeating the
              firm's name beside it reads as a mistake. The name shows only
              when there is no logo to carry it. */}
          {brand.known && !brand.logoUrl && (
            <span className="text-sm font-medium text-muted-foreground" lang={lang}>
              {brand.name ?? t.secure}
            </span>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <Outlet />
      </main>

      <footer className="mx-auto max-w-2xl px-4 pb-12 sm:px-6">
        <p className="text-xs leading-relaxed text-muted-foreground" lang={lang} dir={languageDir(lang)}>
          {t.disclaimer}
        </p>
      </footer>
    </div>
  );
}
